import React, {
  useCallback,
  useEffect,
  useState,
  useContext,
  useMemo,
  useRef,
} from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";
import { UserContext } from "context/user";
import { getFields } from "api/Farm";
import { deleteField } from "api/Field";
import Button from "components/Button";
import Confirm from "components/Confirm";
import AddNewFarmModalContainer from "layouts/AddNewFarmModal";
import { addTextChangeDebounce } from "utils";
import Table from "components/Table";
import FieldNameCell from "./components/FieldNameCell";
import TotalAreaCell from "./components/TotalAreaCell";
import FieldCropTypeCell from "./components/FieldCropTypeCell";
import UpdatedAtCell from "./components/UpdatedAtCell";
import "./index.scss";

const FieldsListContainer = ({ farm }) => {
  const history = useHistory();
  const { token } = useContext(UserContext);
  const [searchString, setSearhString] = useState("");
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageSize, setPageSize] = useState(10);
  const [pageCount, setPageCount] = useState(0);
  const inputRef = useRef(null);

  const fetchData = useCallback(async () => {
    if (!token) return;

    const currentPath = window.location.pathname.split("/");
    let farmID = "";

    if (currentPath[1] === "farms" && currentPath[3] === "details") {
      const farmIdx = 2;
      farmID = currentPath[farmIdx];
    }
    setLoading(true);
    const fieldsData = await getFields(farmID, token);
    setFields(fieldsData?.features);

    const listSize = fieldsData?.features && fieldsData?.features?.length;
    const totalPages = Math.ceil(listSize / currentPageSize);
    setPageCount(totalPages);
    setLoading(false);
  }, [token, currentPageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const removeField = useCallback(
    async (data) => {
      try {
        await deleteField(data?.id, token);
        const fieldsData = await getFields(data?.farm_id, token);
        setFields(fieldsData?.features);
        toast.success("Field deleted successfully!");
      } catch {
        toast.error("Can't delete field!");
      }
    },
    [token]
  );

  const confirmRemoveField = useCallback(
    (data) => {
      const options = {
        title: "Are you sure?",
        message: "Deleting this field will remove all associated layers.",
        buttons: [
          {
            label: "Yes, delete anyway.",
            onClick: () => {
              removeField(data);
            },
            className: "-danger",
          },
          {
            label: "No",
            onClick: () => {},
            className: "",
          },
        ],
        childrenElement: () => <div />,
        customUI: ({ title, message, buttons, onClose }) => (
          <Confirm
            title={title}
            message={message}
            buttons={buttons}
            onClose={onClose}
          />
        ),
        closeOnEscape: false,
        closeOnClickOutside: false,
        overlayClassName: "overlay-custom-class-name",
      };

      confirmAlert(options);
    },
    [removeField]
  );

  const columns = useMemo(
    () => [
      {
        Header: "Field Name",
        accessor: "properties.field_name",
        Cell: (props) => <FieldNameCell {...props} />,
      },
      {
        Header: "Crop Type",
        accessor: "properties.field_crop_type",
        Cell: FieldCropTypeCell,
      },
      {
        Header: "Total Area",
        accessor: "properties.area",
        Cell: TotalAreaCell,
      },
      {
        Header: "Last Updated",
        accessor: "properties.updated_on",
        Cell: (props) => (
          <UpdatedAtCell
            {...props}
            deleteField={(e) => confirmRemoveField(e)}
          />
        ),
      },
    ],
    [confirmRemoveField]
  );

  const getFilteredFields = () => {
    const filterFields =
      fields &&
      [...fields].filter((field) =>
        field?.properties?.field_name
          .toLowerCase()
          .includes(searchString.toLowerCase())
      );
    return filterFields || [];
  };

  const handleSearchFarm = (search) => {
    addTextChangeDebounce(() => {
      inputRef.current.value = 1;
      setCurrentPage(0);
      setSearhString(search);
    });
  };

  const filteredFields = searchString ? getFilteredFields() : fields;
  const listSize = filteredFields?.length && filteredFields?.length;
  const totalPages = Math.ceil(listSize / currentPageSize);
  let startSlice = 0;

  let arrayRowSize = Object.assign(totalPages);
  let endSlice = currentPageSize;
  const paginatedFields = [];

  for (arrayRowSize; arrayRowSize > 0; arrayRowSize -= 1) {
    paginatedFields.push(filteredFields.slice(startSlice, endSlice));
    startSlice += currentPageSize;
    endSlice += currentPageSize;
  }

  const data = paginatedFields[currentPage] || [];

  return (
    <div
      data-testid="farms-and-fields-detail"
      className="farms-and-fields-details"
    >
      <div className="actions-wrapper">
        <div className="left">
          <div className="search">
            <span className="icon-search" />
            <input
              data-testid="search-input"
              placeholder="Search for fields..."
              className="search-input"
              name="searchString"
              onChange={(e) => handleSearchFarm(e.target.value)}
            />
          </div>
        </div>
        <div className="right">
          <Button
            type="solid"
            color="green"
            label="View Farm"
            icon="eye"
            onClick={() => history.push(`/farms/${farm?.properties?.farm_id}`)}
            aria-hidden
          />
          <AddNewFarmModalContainer
            farm={farm}
            label="Create Field"
            color="green"
            type="outlined"
          />
        </div>
      </div>
      <div data-testid="farm-table" className="farm-table field">
        <Table
          data={data || []}
          columns={columns}
          isPaginated={false}
          loading={loading}
        />
        <div className="pagination-wrapper">
          <div className="pagination-size">
            Show
            <select
              className="select"
              value={currentPageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
              }}
            >
              {[5, 10, 15, 20].map((newPageSize) => (
                <option key={newPageSize} value={newPageSize}>
                  {newPageSize}
                </option>
              ))}
            </select>
            Items
          </div>

          <div className="main-container">
            <button
              onClick={() => setCurrentPage(0)}
              disabled={currentPage === 0}
              type="button"
              className="first-page"
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 0}
              type="button"
              className="previous-page"
            >
              <span className="icon-chevron-left" />
              Prev
            </button>
            <span>
              Page{" "}
              <strong>
                {currentPage + 1} of {totalPages}
              </strong>{" "}
            </span>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === pageCount - 1}
              type="button"
              className="next-page"
            >
              Next
              <span className="icon-chevron-right" />
            </button>
            <button
              onClick={() => setCurrentPage(pageCount - 1)}
              disabled={currentPage === pageCount}
              type="button"
              className="last-page"
            >
              Last
            </button>
          </div>
          <div className="page-number">
            Go to page:{" "}
            <input
              ref={inputRef}
              type="number"
              defaultValue={currentPage + 1}
              onChange={(e) => {
                const targetPage = e.target.value
                  ? Number(e.target.value) - 1
                  : 0;
                if (e.target.value <= totalPages) {
                  setCurrentPage(targetPage);
                }
              }}
              max={totalPages}
              min="0"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

FieldsListContainer.propTypes = {
  farm: PropTypes.oneOfType([PropTypes.object]),
};

FieldsListContainer.defaultProps = {
  farm: {},
};

export default FieldsListContainer;
